import React from "react";
import { Link } from "react-router-dom";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";

export interface ManifestSegmentProbill {
  probillNumber?: number | string | null;
  destinationTerminalNumber?: number | string | null;
  destinationTerminalName?: string | null;
  destinationAirCode?: string | null;
  destinationAirportCode?: string | null;
  airCode?: string | null;
  airportCode?: string | null;
  shipmentCount?: number | null;
  handlingUnitCount?: number | null;
  rollCount?: number | null;
  nonRolledCount?: number | null;
  totalWeight?: number | null;
}

const columnHelper = createColumnHelper<ManifestSegmentProbill>();

export const MANIFEST_DETAILS_PROBILL_COLUMN_OPTIONS: { id: string; label: string }[] = [
  { id: "probillNumber", label: "Probill" },
  { id: "destination", label: "Destination" },
  { id: "shipmentCount", label: "Shipments" },
  { id: "handlingUnitCount", label: "HU" },
  { id: "rollCount", label: "Rolls" },
  { id: "nonRolledCount", label: "Pallets" },
  { id: "totalWeight", label: "Weight" },
];

const formatCount = (value?: number | null): string => {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return Number(value).toLocaleString("en", { useGrouping: true, maximumFractionDigits: 0 });
};

const isNonBlank = (v: unknown): boolean =>
  v != null && (typeof v !== "string" || v.trim() !== "");

/** e.g. "ANA - ANAHEIM 109" */
export const formatProbillDestination = (row: ManifestSegmentProbill): string => {
  const airCode = (
    row.destinationAirCode ??
    row.destinationAirportCode ??
    row.airCode ??
    row.airportCode ??
    ""
  )
    .toString()
    .trim();
  const name = (row.destinationTerminalName ?? "").toString().trim();
  const num = row.destinationTerminalNumber;

  if (!isNonBlank(name) && !isNonBlank(airCode)) {
    if (num != null && String(num).trim() !== "") return String(num);
    return "";
  }

  if (name && airCode && name !== airCode) {
    return num != null && String(num).trim() !== ""
      ? `${airCode} - ${name} ${num}`
      : `${airCode} - ${name}`;
  }

  const label = name || airCode;
  return num != null && String(num).trim() !== "" ? `${label} ${num}` : label;
};

export const getManifestDetailsProbillColumns = (): ColumnDef<ManifestSegmentProbill, any>[] => [
  columnHelper.accessor("probillNumber", {
    id: "probillNumber",
    header: "Probill",
    size: 110,
    cell: ({ getValue }) => {
      const value = getValue();
      if (value == null || value === "") return "—";
      return (
        <Link
          className="xgs-table-link-cell"
          to={`/shipments/${value}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {value}
        </Link>
      );
    },
    filterFn: "includesString",
  }),
  columnHelper.accessor((row) => formatProbillDestination(row), {
    id: "destination",
    header: "Destination",
    size: 220,
    cell: ({ getValue }) => getValue() || "—",
    meta: { filterVariant: "select" },
    filterFn: "equalsString",
  }),
  columnHelper.accessor("handlingUnitCount", {
    id: "handlingUnitCount",
    header: "HU",
    size: 70,
    cell: ({ getValue }) => formatCount(getValue()),
    filterFn: "includesString",
  }),
  columnHelper.accessor("rollCount", {
    id: "rollCount",
    header: "Rolls",
    size: 80,
    cell: ({ getValue }) => formatCount(getValue()),
    filterFn: "includesString",
  }),
  columnHelper.accessor("nonRolledCount", {
    id: "nonRolledCount",
    header: "Pallets",
    size: 80,
    cell: ({ getValue }) => formatCount(getValue()),
    filterFn: "includesString",
  }),
  columnHelper.accessor("totalWeight", {
    id: "totalWeight",
    header: "Weight",
    size: 100,
    cell: ({ getValue }) => {
      const v = getValue();
      if (v == null || Number.isNaN(Number(v))) return "—";
      return `${formatCount(v)} lbs`;
    },
    filterFn: "includesString",
  }),
];
